import { useEffect, useRef, useState } from 'react';

export default function Pricing() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section ref={sectionRef} id="pricing" className="py-16 md:py-24 lg:py-32 bg-[#0A1628] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-64 md:w-96 h-64 md:h-96 bg-[#FF6B6B] rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-64 md:w-96 h-64 md:h-96 bg-[#1a3a5c] rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-4 h-4 bg-[#FF6B6B] rounded-full animate-ping" style={{ animationDuration: '3s' }}></div>
      <div className="absolute bottom-40 right-20 w-6 h-6 border-2 border-white/20 rounded-full animate-bounce" style={{ animationDuration: '4s' }}></div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-20">
        <div className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-flex items-center gap-2 bg-white/10 text-white px-4 md:px-5 py-2 rounded-full text-xs md:text-sm font-semibold mb-4 md:mb-6">
            <span className="w-1.5 h-1.5 bg-[#FF6B6B] rounded-full animate-pulse"></span>
            Single price
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            Simple pricing,<br />
            <span className="text-[#FF6B6B] italic">all inclusive</span>
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-300 px-4">
            Perfect for showcase websites that need to be online quickly
          </p>
        </div>

        <div className={`bg-gradient-to-br from-white to-gray-50 rounded-3xl md:rounded-[48px] shadow-2xl overflow-hidden transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="p-6 md:p-10 lg:p-16">
            <div className="text-center mb-8 md:mb-12">
              <span className="inline-block bg-[#FF6B6B] text-white px-4 md:px-6 py-2 rounded-full text-xs md:text-sm font-semibold mb-4 md:mb-6 animate-pulse">
                Complete Package
              </span>
              <div className="mb-4">
                <span className="text-6xl md:text-7xl lg:text-8xl font-bold text-[#0A1628]">$399</span>
              </div>
              <p className="text-lg md:text-xl text-[#0A1628]/60 italic">All inclusive, no surprises</p>
            </div>

            <div className="space-y-4 md:space-y-6 mb-8 md:mb-12">
              <h3 className="text-xl md:text-2xl font-bold text-[#0A1628] mb-4 md:mb-6">What's included:</h3>
              <div className="space-y-3 md:space-y-4">
                {[
                  'Custom showcase website (up to 3 pages)',
                  'Google My Business page creation',
                  'Basic SEO configuration',
                  'Responsive design (mobile, tablet, desktop)',
                  'Fast delivery',
                  '2 free revisions included'
                ].map((item, index) => (
                  <div 
                    key={index} 
                    className={`flex items-start gap-3 md:gap-4 py-2 md:py-3 border-b border-gray-200 last:border-0 transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
                    style={{ transitionDelay: `${400 + index * 100}ms` }}
                  >
                    <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-xl md:text-2xl text-[#FF6B6B] font-bold"></i>
                    </div>
                    <span className="text-[#0A1628]/70 text-sm md:text-base lg:text-lg">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl md:rounded-3xl p-6 md:p-8 mb-8 md:mb-12">
              <h3 className="text-xl md:text-2xl font-bold text-[#0A1628] mb-6 md:mb-8 text-center">How it works:</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { num: '1', text: 'Contact me with your project' },
                  { num: '2', text: 'I create your website' },
                  { num: '3', text: 'You pay after delivery' }
                ].map((step, index) => (
                  <div key={index} className="text-center relative group">
                    <div className="w-14 h-14 md:w-16 md:h-16 bg-[#FF6B6B] text-white rounded-full flex items-center justify-center text-xl md:text-2xl font-bold mx-auto mb-3 md:mb-4 group-hover:scale-110 transition-transform">
                      {step.num}
                    </div>
                    <p className="text-sm md:text-base text-[#0A1628]/70">{step.text}</p>
                    {index < 2 && (
                      <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gray-300">
                        <i className="ri-arrow-right-line absolute right-0 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3 md:space-y-4 mb-8 md:mb-12">
              {[
                { icon: 'ri-shield-check-line', text: 'You own 100% of your website' },
                { icon: 'ri-hand-coin-line', text: 'Payment only after delivery' },
                { icon: 'ri-global-line', text: 'Domain name at your expense (approx. $10-15/year)' }
              ].map((note, index) => (
                <div key={index} className="flex items-start gap-3 md:gap-4 p-3 md:p-4 bg-[#FFF9E5] border-2 border-dashed border-[#FFE082] rounded-xl md:rounded-2xl hover:scale-[1.02] transition-transform cursor-default">
                  <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`${note.icon} text-xl md:text-2xl text-[#F59E0B]`}></i>
                  </div>
                  <p className="text-[#0A1628] text-sm md:text-base font-medium flex-1">{note.text}</p>
                </div>
              ))}
            </div>

            <button 
              onClick={() => scrollToSection('contact')}
              className="w-full bg-[#0A1628] text-white py-4 md:py-5 lg:py-6 rounded-full text-base md:text-lg lg:text-xl font-semibold hover:bg-[#0A1628]/90 transition-all duration-300 hover:scale-[1.02] shadow-lg group whitespace-nowrap cursor-pointer"
            >
              Start my project
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform inline-block"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
